import {
    Bot,
    Check,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    CircleDot,
    GitBranch,
    Sparkles,
} from "lucide-react";
import { characterFor } from "src/stage/characters";

// AUTO-CRM-32: automation steps are done by an agent, shown with Appy.
const agent = characterFor("appy");

function isPathActive(branch, currentNodeId) {
    if (!branch) return false;
    if (branch.node?.id === currentNodeId) return true;
    if (branch.linear_path?.some((step) => step.node?.id === currentNodeId)) return true;
    return branch.branches?.some((child) => isPathActive(child, currentNodeId)) || false;
}

function buildWorkflowChain(workflow, expandedBranches, currentNodeId) {
    const chain = [];
    let cameFromBranch = null;
    let linearPath = workflow.linear_path ? [...workflow.linear_path] : [];
    let branches = workflow.branches || [];

    if (workflow.current_node && !workflow.completed) {
        const currentIsVisible = linearPath.some((step) => step.node?.id === workflow.current_node.id)
            || branches.some((branch) => isPathActive(branch, workflow.current_node.id));

        if (!currentIsVisible) {
            linearPath.unshift({
                transition_id: `current-${workflow.current_node.id}`,
                action: "Current activity",
                node: workflow.current_node,
            });
        }
    }

    while (true) {
        const branchIds = branches.map((branch) => branch.transition_id);
        const showAllKey = branches.length ? `show-all:${branches[0].transition_id}` : null;
        const showAll = Boolean(expandedBranches[showAllKey]);
        const manuallyPicked = branches.find((branch) => expandedBranches[branch.transition_id]);
        const takenBranch = branches.find((branch) => isPathActive(branch, currentNodeId));
        const selectedBranch = showAll ? undefined : (manuallyPicked || takenBranch);

        chain.push({ linearPath, branches, branchIds, showAllKey, selectedBranch, cameFromBranch });

        if (!selectedBranch) break;

        const hasMore = selectedBranch.linear_path?.length || selectedBranch.branches?.length;
        if (!hasMore) break;

        cameFromBranch = selectedBranch;
        linearPath = selectedBranch.linear_path || [];
        branches = selectedBranch.branches || [];
    }

    return chain;
}

function NodeIcon({ type, className = "h-4 w-4" }) {
    const icons = {
        automation: Bot,
        decision: GitBranch,
        activity: CircleDot,
        manual: CircleDot,
        end: CheckCircle2,
    };
    const Icon = icons[type] || Sparkles;

    return <Icon className={className} />;
}

const typeLabels = { automation: "Agent", decision: "Decision", activity: "Activity", manual: "Manual", end: "Done" };

const typeClasses = {
    automation: "bg-sky-50 text-sky-700",
    decision: "bg-amber-50 text-amber-700",
    activity: "bg-blue-50 text-blue-700",
    manual: "bg-blue-50 text-blue-700",
    end: "bg-emerald-50 text-emerald-700",
};

function NodeBadge({ type }) {
    return (
        <span className={`rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${typeClasses[type] || "bg-slate-100 text-slate-600"}`}>
            {typeLabels[type] || type || "Activity"}
        </span>
    );
}

function CompletionAction({ node, currentNodeId, working, completeNode, onComplete }) {
    return (
        <input
            type="checkbox"
            checked={Boolean(node.completed)}
            aria-label={`${node.completed ? "Completed" : "Complete"} ${node.name}`}
            disabled={working || node.completed}
            onChange={() => onComplete ? onComplete() : completeNode(node.id)}
            title={node.completed ? "Completed" : node.id === currentNodeId ? "Complete current step" : "Complete this step and make it active"}
            className="h-5 w-5 shrink-0 cursor-pointer rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-default"
        />
    );
}

function LinearStep({ step, isCurrent, isLast, requiresChoice, currentNodeId, working, completeNode }) {
    const node = step.node;

    return (
        <div className="relative flex gap-3">
            {!isLast && <div className="absolute bottom-[-18px] left-[19px] top-10 w-px bg-slate-200" />}
            <div className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${isCurrent ? "border-blue-200 bg-blue-600 text-white shadow-md shadow-blue-100" : node.completed ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-slate-200 bg-white text-slate-400"}`}>
                {node.completed ? <Check className="h-4 w-4" /> : node.node_type === "automation" && !isCurrent && agent
                    ? <img src={agent.image} alt="" className="h-8 w-8 rounded-lg object-cover" title="An agent does this step" />
                    : <NodeIcon type={node.node_type} />}
            </div>

            <div className={`mb-3 flex min-w-0 flex-1 items-center justify-between gap-4 rounded-xl border px-4 py-3 ${isCurrent ? "border-blue-200 bg-blue-50/60" : "border-slate-200 bg-white"}`}>
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <NodeBadge type={node.node_type} />
                        <span className="text-xs font-medium text-slate-400">{step.action || "Continue"}</span>
                        {isCurrent && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700">Now</span>}
                    </div>
                    <h4 className="mt-1.5 truncate text-sm font-semibold text-slate-900">{node.name}</h4>
                    {node.completed_by?.name && <p className="mt-1 text-xs text-slate-500">Completed by {node.completed_by.name}</p>}
                </div>
                <div className="flex items-center gap-2">
                    {requiresChoice && !node.completed ? (
                        <span className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Choose outcome</span>
                    ) : (
                        <>
                            <CompletionAction node={node} currentNodeId={currentNodeId} working={working} completeNode={completeNode} />
                            <span className="hidden text-[10px] font-semibold text-slate-400 sm:inline">{node.completed ? "Done" : "Check off"}</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function BranchChoices({ level, currentNodeId, working, completeNode, toggleBranch }) {
    const branchSource = level.linearPath?.[level.linearPath.length - 1]?.node;

    return (
        <section className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/70 p-4">
            <div className="mb-3 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700"><GitBranch className="h-4 w-4" /></div>
                <div>
                    <h4 className="text-sm font-semibold text-slate-900">Choose the next path</h4>
                    <p className="text-xs text-slate-500">Preview one outcome before continuing.</p>
                </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
                {level.branches.map((branch) => {
                    const active = isPathActive(branch, currentNodeId);

                    return (
                        <div
                            key={branch.transition_id}
                            className={`group flex items-center rounded-xl border bg-white pr-3 transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-sm ${active ? "border-blue-300 ring-2 ring-blue-100" : "border-slate-200"}`}
                        >
                            <button
                                type="button"
                                disabled={working}
                                onClick={() => toggleBranch(branch.transition_id, [...level.branchIds, level.showAllKey], true)}
                                className="flex min-w-0 flex-1 items-center gap-3 p-3 text-left disabled:opacity-50"
                            >
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700"><NodeIcon type={branch.node.node_type} /></span>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-semibold text-slate-900">{branch.action || branch.node.name}</span>
                                    <span className="block truncate text-xs text-slate-500">{branch.node.name}</span>
                                </span>
                                <ChevronRight className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-blue-600" />
                            </button>
                            <CompletionAction
                                node={branch.node}
                                currentNodeId={currentNodeId}
                                working={working}
                                completeNode={completeNode}
                                onComplete={branchSource ? () => completeNode(branchSource.id, branch.node.id) : undefined}
                            />
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function SelectedBranch({ level, currentNodeId, working, completeNode, toggleBranch }) {
    const branch = level.selectedBranch;
    const isCurrent = branch.node.id === currentNodeId;
    const branchSource = level.linearPath?.[level.linearPath.length - 1]?.node;

    return (
        <section className="mt-4 rounded-xl border border-blue-200 bg-blue-50/40 p-4">
            <button type="button" onClick={() => toggleBranch(level.showAllKey, level.branchIds, true)} className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-blue-700">
                <ChevronLeft className="h-4 w-4" /> Change path
            </button>
            <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-white p-3 shadow-sm">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><NodeIcon type={branch.node.node_type} /></span>
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-blue-600">{branch.action || "Selected path"}</p>
                    <h4 className="truncate text-sm font-semibold text-slate-900">{branch.node.name}</h4>
                </div>
                <div className="flex items-center gap-2">
                    {!isCurrent && !branch.node.completed && <span className="text-[10px] font-semibold text-slate-400">Check to take path</span>}
                    <CompletionAction
                        node={branch.node}
                        currentNodeId={currentNodeId}
                        working={working}
                        completeNode={completeNode}
                        onComplete={branchSource ? () => completeNode(branchSource.id, branch.node.id) : undefined}
                    />
                </div>
            </div>
        </section>
    );
}

export default function WorkflowPath({ workflow, working, completeNode, toggleBranch, expandedBranches, currentNodeId }) {
    if (!workflow) return null;

    const chain = buildWorkflowChain(workflow, expandedBranches, currentNodeId);
    const level = chain[chain.length - 1];
    const parentLevel = chain.length > 1 ? chain[chain.length - 2] : null;

    return (
        <div>
            {level.cameFromBranch && parentLevel && (
                <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-blue-200 bg-blue-50/50 p-2">
                    <button type="button" onClick={() => toggleBranch(parentLevel.showAllKey, parentLevel.branchIds, true)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-2 text-xs font-semibold text-slate-600 hover:bg-white hover:text-blue-700">
                        <ChevronLeft className="h-4 w-4" /> Change path
                    </button>
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-700">Previewing: {level.cameFromBranch.action || level.cameFromBranch.node.name}</span>
                    {isPathActive(level.cameFromBranch, currentNodeId) ? (
                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Active path</span>
                    ) : (
                        <CompletionAction node={level.cameFromBranch.node} currentNodeId={currentNodeId} working={working} completeNode={completeNode} />
                    )}
                </div>
            )}

            {level.linearPath?.map((step, index) => (
                <LinearStep
                    key={step.transition_id}
                    step={step}
                    isCurrent={step.node.id === currentNodeId}
                    isLast={index === level.linearPath.length - 1}
                    requiresChoice={index === level.linearPath.length - 1 && level.branches.length > 0}
                    currentNodeId={currentNodeId}
                    working={working}
                    completeNode={completeNode}
                />
            ))}

            {level.branches.length > 0 && !level.selectedBranch && (
                <BranchChoices level={level} currentNodeId={currentNodeId} working={working} completeNode={completeNode} toggleBranch={toggleBranch} />
            )}

            {level.selectedBranch && (
                <SelectedBranch level={level} currentNodeId={currentNodeId} working={working} completeNode={completeNode} toggleBranch={toggleBranch} />
            )}
        </div>
    );
}
